import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  HelpCircle,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { StatCard } from "../../components/cards/StatCard";
import { HospitalSelector } from "../../components/dashboard/HospitalSelector";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { cn } from "../../lib/utils";
import { useTranslation } from "../../i18n";
import { AlertsPage } from "./AlertsPage";
import { useSort, SortHeader } from "../../components/ui/sortable";
import { ComplianceRateSummary, ComplianceTrend, HospitalRateTable } from "../../components/compliance/ComplianceRate";
import {
  fetchComplianceStatus,
  fetchComplianceRules,
  createComplianceRule,
  updateComplianceRule,
  deleteComplianceRule,
  type CheckpointStatus,
  type ComplianceAlert,
  type ComplianceRule,
  type ComplianceStatusResponse,
  type ComplianceStatus as CheckpointComplianceStatus,
} from "../../services/compliance";

/**
 * Supervisor view of patrol compliance.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * This page reports STATUS, not assignments. It deliberately has no "next
 * checkpoint", no route, no suggested order and no ETA, and the advisory banner
 * below says so in plain terms. Rows are sorted most-time-critical-first purely
 * so the tightest entries read first — that is a display convenience, not a
 * recommended sequence. Which checkpoints are scanned and in what order remain
 * the officer's and supervisor's decision under their post orders.
 *
 * If you are adding to this page, keep that framing. See the matching notes on
 * api/compliance/status.ts and api/compliance/due.ts.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const REFRESH_INTERVAL_MS = 60_000;

type StatusFilter = "all" | "attention" | "overdue";

const STATUS_STYLES: Record<CheckpointComplianceStatus, { pill: string; label: string }> = {
  OVERDUE: { pill: "bg-red-100 text-red-800 border-red-200", label: "Overdue" },
  DUE_SOON: { pill: "bg-amber-100 text-amber-800 border-amber-200", label: "Due Soon" },
  OK: { pill: "bg-green-100 text-green-800 border-green-200", label: "On Interval" },
  NEVER_SCANNED: { pill: "bg-gray-100 text-gray-600 border-gray-200", label: "No Scan on Record" },
};

function StatusPill({ status }: { status: CheckpointComplianceStatus }) {
  const { t } = useTranslation();
  const style = STATUS_STYLES[status];
  return (
    <span
      className={cn(
        "inline-block whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium",
        style.pill
      )}
    >
      {t(`compliance.status.${status}`, style.label)}
    </span>
  );
}

export const CompliancePage: React.FC = () => {
  const { t, lang } = useTranslation();

  const [hospitalId, setHospitalId] = useStickyHospital();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("attention");
  const [data, setData] = useState<ComplianceStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [openAlertsOpen, setOpenAlertsOpen] = useState(true);
  const [checkpointsOpen, setCheckpointsOpen] = useState(true);

  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(lang, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    [lang]
  );

  const formatTime = useCallback(
    (iso: string | null) => (iso ? dateFormat.format(new Date(iso)) : "—"),
    [dateFormat]
  );

  const load = useCallback(
    async (isBackground = false) => {
      if (isBackground) setRefreshing(true);
      else setLoading(true);
      try {
        const result = await fetchComplianceStatus({
          hospitalId,
          status: statusFilter,
          limit: 500,
        });
        setData(result);
        setError(null);
      } catch (err: any) {
        setError(err?.message || t("compliance.loadFailed", "Could not load compliance status"));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [hospitalId, statusFilter, t]
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Background refresh. Skipped while the tab is hidden so a dashboard left open
  // overnight is not polling the compliance query every minute for nobody.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load(true);
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const counts = data?.counts;
  const checkpoints = data?.checkpoints ?? [];
  const alerts = data?.alerts ?? [];

  // Click-to-sort on the checkpoint table. No sort selected = the API's
  // time-criticality ordering (most overdue first).
  const cpSort = useSort(checkpoints, {
    checkpoint: (c) => c.name,
    status: (c) => c.status,
    last_scan: (c) => c.last_scanned_at,
    interval: (c) => c.interval_minutes,
    remaining: (c) => (c.status === "OVERDUE" ? -c.minutes_overdue : c.minutes_remaining),
    scans24: (c) => c.scans_last_24h,
  });

  return (
    <div className="space-y-4 p-4 md:space-y-6 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800 md:text-2xl">
            {t("compliance.title", "Patrol Compliance")}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {t(
              "compliance.subtitle2h",
              "Checkpoint scan status against the 2-hour scan interval that applies to every checkpoint."
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HospitalSelector selectedHospitalId={hospitalId} onHospitalChange={setHospitalId} />
          {/* Intervals are fixed at 2 hours for every checkpoint (client policy,
              lib/compliance.ts ENFORCE_FIXED_INTERVAL), so the per-site interval
              editor is no longer offered. */}
          <span
            className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
            title={t("compliance.fixedIntervalTip", "Every checkpoint must be scanned at least once every 2 hours.")}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {t("compliance.fixedInterval", "Scan interval: 2 hours")}
          </span>
          <button
            onClick={() => void load(true)}
            disabled={refreshing}
            className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            {t("common.refresh", "Refresh")}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <span>{error}</span>
          <button onClick={() => void load()} className="font-semibold underline">
            {t("common.retry", "Retry")}
          </button>
        </div>
      )}

      {/* 2-hour compliance rate — the headline the client tracks. */}
      <ComplianceRateSummary rate={data?.rate} />

      {/* Summary tiles — always the full estate, never narrowed by the row filter. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title={t("compliance.overdue", "Overdue")}
          value={counts?.overdue ?? "—"}
          subtitle={t("compliance.overdueSub", "Past the scan interval")}
          icon={AlertTriangle}
          iconColor="danger"
        />
        <StatCard
          title={t("compliance.dueSoon", "Due Soon")}
          value={counts?.due_soon ?? "—"}
          subtitle={t("compliance.dueSoonSub", "In the last quarter of the interval")}
          icon={Clock}
          iconColor="warning"
        />
        <StatCard
          title={t("compliance.onInterval", "On Interval")}
          value={counts?.ok ?? "—"}
          subtitle={t("compliance.onIntervalSub", "Scanned within the interval")}
          icon={CheckCircle2}
          iconColor="success"
        />
        <StatCard
          title={t("compliance.neverScanned", "No Scan on Record")}
          value={counts?.never_scanned ?? "—"}
          subtitle={t("compliance.neverScannedSub2h", "Counts as non-compliant; no alert raised")}
          icon={HelpCircle}
          iconColor="primary"
        />
      </div>

      {/* Never-scanned explainer. Only worth showing when there are some. */}
      {(counts?.never_scanned ?? 0) > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          <span className="font-semibold">
            {t("compliance.neverScannedNoteTitle2h", "Checkpoints with no scan on record:")}
          </span>{" "}
          {t(
            "compliance.neverScannedNote2h",
            "These have not been scanned in the past 2 hours, so they count as non-compliant in the 2-hour rate. They do not raise overdue alerts, because a checkpoint that has never been scanned has no last scan to measure from; this keeps newly added checkpoints from triggering a flood of alerts."
          )}
        </div>
      )}

      {/* 2-hour rate by hospital (now) and over time (from 15-minute snapshots). */}
      <HospitalRateTable rows={data?.rate_by_hospital} />
      <ComplianceTrend hospitalId={hospitalId} />

      {/* Open alerts */}
      {alerts.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button
            onClick={() => setOpenAlertsOpen((o) => !o)}
            className="flex w-full items-center justify-between border-b border-gray-200 px-6 py-4 text-left hover:bg-gray-50"
          >
            <h2 className="font-semibold text-gray-800">
              {t("compliance.openAlerts", "Open Compliance Alerts")}
              <span className="ml-2 rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                {alerts.length}
              </span>
            </h2>
            <span className="text-xs font-medium text-gray-500">
              {openAlertsOpen ? t("common.collapse", "Collapse") : t("common.expand", "Expand")}
            </span>
          </button>
          {openAlertsOpen && (
          <ul className="max-h-80 divide-y divide-gray-200 overflow-y-auto">
            {alerts.map((alert: ComplianceAlert) => (
              <li key={alert.id} className="flex items-start gap-3 px-6 py-3">
                <AlertTriangle
                  className={cn(
                    "mt-0.5 h-4 w-4 flex-shrink-0",
                    alert.severity === "CRITICAL" ? "text-red-600" : "text-amber-600"
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800">{alert.message}</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {[alert.hospital_name, alert.location_name].filter(Boolean).join(" · ")}
                    {" — "}
                    {formatTime(alert.created_at)}
                  </p>
                </div>
                <span
                  className={cn(
                    "whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium",
                    alert.severity === "CRITICAL"
                      ? "border-red-200 bg-red-100 text-red-800"
                      : "border-amber-200 bg-amber-100 text-amber-800"
                  )}
                >
                  {alert.severity}
                </span>
              </li>
            ))}
          </ul>
          )}
        </div>
      )}

      {/* Order per Morgan: the compliance engine's own open alerts first,
          then the Security Alerts section (whose patrol-scan-exceptions panel
          sits below them), then the checkpoint table. */}
      <div className="border-t border-gray-200 pt-6">
        <AlertsPage embedded />
      </div>


      {/* Checkpoint table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-semibold text-gray-800">
            {t("compliance.checkpointStatus", "Checkpoint Status")}
            <span className="ml-2 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {checkpoints.length}
            </span>
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCheckpointsOpen((o) => !o)}
              className="text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              {checkpointsOpen ? t("common.collapse", "Collapse") : t("common.expand", "Expand")}
            </button>
            <label htmlFor="compliance-filter" className="text-sm text-gray-600">
              {t("common.filter", "Filter")}
            </label>
            <select
              id="compliance-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="attention">
                {t("compliance.filterAttention", "Overdue and due soon")}
              </option>
              <option value="overdue">{t("compliance.filterOverdue", "Overdue only")}</option>
              <option value="all">{t("compliance.filterAll", "All checkpoints")}</option>
            </select>
          </div>
        </div>

        {checkpointsOpen && (loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : checkpoints.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <ShieldCheck className="mb-4 h-12 w-12 text-gray-300" />
            <p className="text-gray-500">
              {statusFilter === "all"
                ? t("compliance.emptyAll", "No checkpoints found for this selection.")
                : t("compliance.emptyAttention", "No checkpoints are overdue or due soon.")}
            </p>
          </div>
        ) : (
          <>
            <div className="max-h-[36rem] overflow-x-auto overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 border-b border-gray-200 bg-gray-50">
                  <tr>
                    <SortHeader label={t("compliance.colCheckpoint", "Checkpoint")} k="checkpoint" sort={cpSort.sort} onToggle={cpSort.toggle} />
                    <SortHeader label={t("compliance.colStatus", "Status")} k="status" sort={cpSort.sort} onToggle={cpSort.toggle} />
                    <SortHeader label={t("compliance.colLastScan", "Last Scan")} k="last_scan" sort={cpSort.sort} onToggle={cpSort.toggle} />
                    <SortHeader label={t("compliance.colInterval", "Interval")} k="interval" sort={cpSort.sort} onToggle={cpSort.toggle} />
                    <SortHeader label={t("compliance.colRemaining", "Time in Interval")} k="remaining" sort={cpSort.sort} onToggle={cpSort.toggle} />
                    <SortHeader label={t("compliance.colScans24h", "Scans (24h)")} k="scans24" sort={cpSort.sort} onToggle={cpSort.toggle} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {cpSort.sorted.map((c: CheckpointStatus) => (
                    <tr key={c.location_id} className="transition-colors hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-800">{c.name}</div>
                        <div className="text-xs text-gray-500">
                          {[c.hospital_name, c.place].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <StatusPill status={c.status} />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {formatTime(c.last_scanned_at)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {c.interval_minutes} {t("compliance.minutesShort", "min")}
                        {c.rule_source !== "SYSTEM" && (
                          <span className="ml-1 text-xs text-gray-400">
                            (
                            {c.rule_source === "LOCATION"
                              ? t("compliance.ruleLocation", "location rule")
                              : t("compliance.ruleHospital", "site default")}
                            )
                          </span>
                        )}
                      </td>
                      <td
                        className={cn(
                          "whitespace-nowrap px-6 py-4 text-sm",
                          c.status === "OVERDUE"
                            ? "font-medium text-red-700"
                            : c.status === "DUE_SOON"
                              ? "font-medium text-amber-700"
                              : "text-gray-700"
                        )}
                      >
                        {c.countdown_label}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {c.scans_last_24h}
                        <span className="text-xs text-gray-400"> / {c.min_scans_per_24h}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data?.truncated && (
              <div className="border-t border-gray-200 px-6 py-3 text-xs text-gray-500">
                {t(
                  "compliance.truncated",
                  "Showing the most time-critical results only. Narrow by site to see the rest."
                )}
              </div>
            )}
          </>
        ))}
      </div>

      {data?.generated_at && (
        <p className="text-xs text-gray-400">
          {t("compliance.generatedAt", "Status as of")} {formatTime(data.generated_at)}
        </p>
      )}

      {showRules && (
        <RulesDrawer
          hospitalId={hospitalId}
          onClose={() => setShowRules(false)}
          onChanged={() => void load(true)}
        />
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
 * Scan interval rules
 *
 * Rule precedence is location rule → site default → system default. A hospital
 * with no rows at all is not misconfigured; it simply uses the system default.
 * ──────────────────────────────────────────────────────────────────────────── */

interface RulesDrawerProps {
  hospitalId: string | undefined;
  onClose: () => void;
  onChanged: () => void;
}

const RulesDrawer: React.FC<RulesDrawerProps> = ({ hospitalId, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [rules, setRules] = useState<ComplianceRule[]>([]);
  const [systemDefault, setSystemDefault] = useState<{
    interval_minutes: number;
    min_scans_per_24h: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { interval: string; minScans: string }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchComplianceRules(hospitalId);
      setRules(result.rules);
      setSystemDefault(result.system_default);
      setDrafts(
        Object.fromEntries(
          result.rules.map((r) => [
            r.id,
            { interval: String(r.interval_minutes), minScans: String(r.min_scans_per_24h) },
          ])
        )
      );
      setError(null);
    } catch (err: any) {
      setError(err?.message || t("compliance.rulesLoadFailed", "Could not load rules"));
    } finally {
      setLoading(false);
    }
  }, [hospitalId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (rule: ComplianceRule) => {
    const draft = drafts[rule.id];
    if (!draft) return;
    const interval = parseInt(draft.interval, 10);
    const minScans = parseInt(draft.minScans, 10);
    if (!Number.isFinite(interval) || !Number.isFinite(minScans)) {
      setError(t("compliance.rulesInvalid", "Interval and daily minimum must be numbers"));
      return;
    }

    setSaving(rule.id);
    try {
      await updateComplianceRule(rule.id, {
        interval_minutes: interval,
        min_scans_per_24h: minScans,
      });
      setError(null);
      await load();
      onChanged();
    } catch (err: any) {
      setError(err?.message || t("compliance.rulesSaveFailed", "Could not save rule"));
    } finally {
      setSaving(null);
    }
  };

  const remove = async (rule: ComplianceRule) => {
    setSaving(rule.id);
    try {
      await deleteComplianceRule(rule.id);
      setError(null);
      await load();
      onChanged();
    } catch (err: any) {
      setError(err?.message || t("compliance.rulesDeleteFailed", "Could not delete rule"));
    } finally {
      setSaving(null);
    }
  };

  const addSiteDefault = async () => {
    if (!hospitalId || !systemDefault) return;
    setSaving("new");
    try {
      await createComplianceRule({
        hospital_id: hospitalId,
        location_id: null,
        interval_minutes: systemDefault.interval_minutes,
        min_scans_per_24h: systemDefault.min_scans_per_24h,
      });
      setError(null);
      await load();
      onChanged();
    } catch (err: any) {
      setError(err?.message || t("compliance.rulesCreateFailed", "Could not create rule"));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              {t("compliance.rulesTitle", "Scan Intervals")}
            </h2>
            <p className="mt-0.5 text-sm text-gray-600">
              {t(
                "compliance.rulesSubtitle",
                "A location rule overrides the site default, which overrides the system default."
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100"
            aria-label={t("common.close", "Close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          {systemDefault && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <span className="font-semibold">
                {t("compliance.systemDefault", "System default")}:
              </span>{" "}
              {t("compliance.systemDefaultValue", "every")} {systemDefault.interval_minutes}{" "}
              {t("compliance.minutesShort", "min")}, {systemDefault.min_scans_per_24h}{" "}
              {t("compliance.perDay", "per day")}.{" "}
              {t(
                "compliance.systemDefaultNote",
                "Sites with no rule of their own are measured against this."
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : rules.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <SlidersHorizontal className="mb-4 h-12 w-12 text-gray-300" />
              <p className="text-gray-500">
                {t("compliance.rulesEmpty", "No custom rules — the system default applies.")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t("compliance.colAppliesTo", "Applies to")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t("compliance.colIntervalMin", "Interval (min)")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t("compliance.colMinPerDay", "Min / 24h")}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t("compliance.colActions", "Actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rules.map((rule) => (
                    <tr key={rule.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">
                          {rule.location_name ??
                            t("compliance.siteWide", "All checkpoints at this site")}
                        </div>
                        <div className="text-xs text-gray-500">{rule.hospital_name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min={5}
                          max={10080}
                          value={drafts[rule.id]?.interval ?? ""}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [rule.id]: { ...d[rule.id], interval: e.target.value },
                            }))
                          }
                          className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min={0}
                          max={500}
                          value={drafts[rule.id]?.minScans ?? ""}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [rule.id]: { ...d[rule.id], minScans: e.target.value },
                            }))
                          }
                          className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => void save(rule)}
                          disabled={saving === rule.id}
                          className="mr-3 text-sm font-medium text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                        >
                          {t("common.save", "Save")}
                        </button>
                        <button
                          onClick={() => void remove(rule)}
                          disabled={saving === rule.id}
                          className="text-sm font-medium text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          {t("common.delete", "Delete")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <div className="text-sm text-gray-500">
            {!hospitalId &&
              t("compliance.pickSiteToAdd", "Select a single site to add a default for it.")}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => void addSiteDefault()}
              disabled={!hospitalId || saving === "new"}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {t("compliance.addSiteDefault", "Add site default")}
            </button>
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            >
              {t("common.close", "Close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
